import { isAxiosError } from "axios";
import { useQuery } from "react-query";
import { z } from "zod";

import { loggedInApiCall } from "src/api/axiosClient/userApi";

import { zodParseWithLog } from "../../apiErrorLogs";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { BrandListSchema, type BrandList } from "./brandSchemas";

export { BrandListSchema };
export type { BrandList } from "./brandSchemas";

export const getAllBrandsKey = "allBrands" as const;

type Options = {
    enabled?: boolean;
    itemsPerPage?: number;
    page?: number;
    initSearch?: string;
    name?: string;
};

type BrandCollectionData = {
    items: BrandList[];
    totalItems: number;
};

export type BrandCollectionState =
    | { status: "disabled"; items: BrandList[]; totalItems: number }
    | { status: "loading" }
    | { status: "success-value"; items: BrandList[]; totalItems: number }
    | { status: "empty"; items: BrandList[]; totalItems: number }
    | { status: "forbidden" }
    | { status: "not-found" }
    | { status: "contract-error" }
    | { status: "server-error" };

export type BrandCollectionErrorState = Exclude<
    BrandCollectionState,
    { status: "disabled" } | { status: "loading" } | { status: "success-value" } | { status: "empty" }
>;

export const isBrandCollectionError = (state: BrandCollectionState): state is BrandCollectionErrorState =>
    state.status === "contract-error" ||
    state.status === "forbidden" ||
    state.status === "not-found" ||
    state.status === "server-error";

type BrandCollectionStateInput = {
    data: BrandCollectionData | undefined;
    enabled?: boolean;
    error: unknown;
    isLoading: boolean;
};

export const getBrandCollectionState = (input: BrandCollectionStateInput): BrandCollectionState => {
    const { data, enabled = true, error, isLoading } = input;

    if (!enabled) {
        return { status: "disabled", items: [], totalItems: 0 };
    }

    if (error instanceof z.ZodError) {
        return { status: "contract-error" };
    }

    if (isAxiosError(error)) {
        if (error.response?.status === 403) {
            return { status: "forbidden" };
        }

        if (error.response?.status === 404) {
            return { status: "not-found" };
        }

        return { status: "server-error" };
    }

    if (error !== null && error !== undefined) {
        return { status: "server-error" };
    }

    if (isLoading || data === undefined) {
        return { status: "loading" };
    }

    if (data.items.length === 0) {
        return { status: "empty", ...data };
    }

    return { status: "success-value", ...data };
};

export const useGetAllBrands = (options?: Options) => {
    const { enabled = true, itemsPerPage = 20, page = 1, initSearch = "", name } = options || {};
    const { selectedCompany } = useFoodpilotContext();
    const organizationId = selectedCompany?.id;
    const isEnabled = enabled && organizationId !== undefined;

    const searchApi = useDelayedSearch({ startSearch: initSearch });

    const query = useQuery({
        queryKey: [getAllBrandsKey, organizationId ?? null, { itemsPerPage, page }, name ?? searchApi.delayedSearch],
        enabled: isEnabled,
        keepPreviousData: false,
        queryFn: async () => {
            if (!enabled || organizationId === undefined) {
                throw new Error("Cannot load brands while disabled or without a selected organization");
            }

            const url = `/api/brands`;
            return await loggedInApiCall.get(url, {
                params: {
                    "order[name]": "asc",
                    itemsPerPage: itemsPerPage,
                    page: page,
                    name: (name ?? searchApi.delayedSearch) || null,
                    organization_id: organizationId,
                },
            });
        },
        select: (request) => {
            const totalItems = z.number().parse(request.data["hydra:totalItems"]);
            const brands = request.data["hydra:member"];
            const data = zodParseWithLog(brands, BrandListSchema.array(), getAllBrandsKey);
            return {
                items: data,
                totalItems,
            };
        },
    });

    const brandCollectionState = getBrandCollectionState({
        data: query.data,
        enabled: isEnabled,
        error: query.error,
        isLoading: query.isLoading,
    });
    const isSuccess = brandCollectionState.status === "success-value" || brandCollectionState.status === "empty";

    return {
        allBrands: isSuccess ? brandCollectionState.items : undefined,
        totalItems: isSuccess ? brandCollectionState.totalItems : undefined,
        brandCollectionState,
        searchApi,
        ...query,
        data: isEnabled ? query.data : undefined,
    };
};
